import React, { useState } from 'react';

import { useIntl } from 'react-intl';
import { Col, ListGroup } from 'react-bootstrap';
import { encode } from 'html-entities';

import { useAppSelector } from 'hooks';
import { CalendarIcon } from 'assets/icons';
import { formatRecurrence } from 'utils/Time';
import { DropdownGroup } from 'components/Forms';
import { isValidUrl } from 'utils/Helpers/Url';

import AddEditEducationalContentModal from './AddEditEducationalContentModal';
import DeleteEducationalContentModal from './DeleteEducationalContentModal';

export const Content = ({
  educationalContent
}: {
  educationalContent: ICarePlanEducationalContent;
}) => {
  const intl = useIntl();
  const locale = useAppSelector((state) => state.intl.locale);

  return (
    <div className="p-3 bg-light font-weight-bolder">
      <div className="text-sm font-weight-bolder white-space-break-spaces">
        <ListGroup className="p-3">
          {educationalContent.articles.map((article, index) => (
            <ListGroup.Item key={index} className="overflow-auto">
              {(!article.url && !article.title) || !isValidUrl(article.url) ? (
                <span>-</span>
              ) : (
                <a
                  href={encode(article.url)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-truncate"
                >
                  {article.title || article.url}
                </a>
              )}
            </ListGroup.Item>
          ))}
        </ListGroup>
      </div>
      {educationalContent.recurrence_hr && (
        <div className="text-sm font-weight-bolder mt-2">
          <CalendarIcon className="icon me-2" style={{ fontSize: '14px' }} />
          {formatRecurrence(
            educationalContent.recurrence_hr,
            locale,
            intl.formatMessage({ id: 'atKey' })
          )}
        </div>
      )}
    </div>
  );
};

interface IEducationalContentContainerProps {
  educationalContent: ICarePlanEducationalContent;
  onDelete: (id: string) => any;
  handleEdit: any;
  carePlanTemplate?: ICarePlanTemplate;
  dataTestId: string;
  disabled?: boolean;
}

const EducationalContent = ({
  educationalContent,
  onDelete,
  handleEdit,
  carePlanTemplate,
  dataTestId,
  disabled
}: IEducationalContentContainerProps) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const isTemplateGlobal = carePlanTemplate?.template_type === 'GLOBAL';

  const handleEditEducationalContent = () => {
    setShowEditModal(false);
    handleEdit();
  };

  const handleDeleteEducationalContent = () => {
    onDelete(educationalContent.id);
  };

  return (
    <Col lg={6} xl={4} className="p-3" data-test={dataTestId}>
      <div className="bg-light">
        {!isTemplateGlobal && (
          <DropdownGroup
            dataTestId={dataTestId}
            onShowEditModal={() => setShowEditModal(true)}
            onShowDeleteModal={() => setShowDeleteModal(true)}
            disabled={disabled}
          />
        )}
        <Content educationalContent={educationalContent} />
      </div>

      {showEditModal && (
        <AddEditEducationalContentModal
          show={showEditModal}
          handleClose={() => setShowEditModal(false)}
          handleEdit={handleEditEducationalContent}
          educationalContent={educationalContent}
          carePlanTemplate={carePlanTemplate}
        />
      )}
      <DeleteEducationalContentModal
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        educationalContent={educationalContent}
        onDelete={handleDeleteEducationalContent}
        carePlanTemplate={carePlanTemplate}
      />
    </Col>
  );
};

export default EducationalContent;
